import { Popover, Transition } from '@headlessui/react'
import InlineSkeleton from 'components/common/InlineSkeleton'
import IntLink from 'components/common/IntLink'
import LoadingOverlay from 'components/common/LoadingOverlay'
import Button from 'components/forms/Button'
import UserCircleIcon from 'components/icons/UserCircleIcon'
import useSlug from 'hooks/useSlug'
import { isAbsent, isNonEmptyString, isPresent } from 'lib/utils'

import { Fragment, useCallback, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  selectUser,
  useLazyGetCartQuery,
  useLogoutMutation,
} from 'store/api'
import { removeUserToken, selectUserToken } from 'store/auth'

type Props = {
  setIsOpenCb: React.Dispatch<React.SetStateAction<boolean>>
}

let HeaderAuth = ({ setIsOpenCb }: Props) => {
  let { queryLng } = useSlug()
  let dispatch = useDispatch()

  let userToken = useSelector(selectUserToken)
  let user = useSelector(selectUser(queryLng))

  let [
    logout,
    { isSuccess: isLogoutSuccess, isLoading: isLogoutLoading },
  ] = useLogoutMutation()

  let [getCart] = useLazyGetCartQuery()

  useEffect(() => {
    if (isLogoutSuccess) {
      dispatch(removeUserToken())
      void getCart(queryLng)
    }
  }, [dispatch, isLogoutSuccess, getCart, queryLng])

  let handleClick = useCallback(() => {
    isAbsent(userToken) && setIsOpenCb((prev) => !prev)
  }, [userToken, setIsOpenCb])

  return (
    <>
      <Popover className='hidden lg:block relative'>
        {({ close }) => (
          <>
            <Popover.Button
              as={Button}
              aria-label={
                isPresent(userToken) ? 'Личный кабинет' : 'Войти'
              }
              className='group p-0'
              onClick={handleClick}
            >
              <div className='flex gap-3 items-center'>
                <UserCircleIcon
                  className='h-6 w-6 text-accent flex-shrink-0'
                  aria-hidden='true'
                />
                <span className='hover:text-accent text-base2 leading-none translate-y-0.5'>
                  {isPresent(userToken) ? 'Личный кабинет' : 'Войти'}
                </span>
              </div>
            </Popover.Button>

            <Transition
              as={Fragment}
              enter='transition ease-out duration-200'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='transition ease-in duration-150'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
            >
              <Popover.Panel className='absolute w-60 top-8 border border-lightGrey bg-white left-0 shadow-md overflow-hidden'>
                <div className='relative p-8'>
                  {isNonEmptyString(user.first_name) && (
                    <div className='tracking-tight font-extrabold mb-3'>
                      {user.first_name}
                    </div>
                  )}

                  <div className='tracking-tight'>
                    {isNonEmptyString(user.phone) ? (
                      <>
                        +7 {user.phone.slice(0, 3)}{' '}
                        {user.phone.slice(3, 6)}-
                        {user.phone.slice(6, 8)}-{user.phone.slice(8)}
                      </>
                    ) : (
                      <InlineSkeleton className='skeleton-gray w-36' />
                    )}
                  </div>

                  <div className='mt-10' onClick={() => close()}>
                    <IntLink
                      href='/account'
                      className='block font-bold tracking-tight hover:text-accent'
                    >
                      Личные данные
                    </IntLink>
                  </div>

                  <div onClick={() => close()}>
                    <IntLink
                      href='/account'
                      className='mt-4 block font-bold tracking-tight hover:hover:text-accent'
                    >
                      Мои адреса
                    </IntLink>
                  </div>

                  <div onClick={() => close()}>
                    <IntLink
                      href='/account/orders'
                      className='mt-4 block font-bold tracking-tight hover:hover:text-accent'
                    >
                      История заказов
                    </IntLink>
                  </div>

                  <div
                    onClick={() => {
                      close()
                      void logout(queryLng)
                    }}
                  >
                    <IntLink
                      href='/account/orders'
                      className='mt-10 block font-bold tracking-tight text-accent'
                    >
                      Выход
                    </IntLink>
                  </div>
                </div>
              </Popover.Panel>
            </Transition>
          </>
        )}
      </Popover>

      <LoadingOverlay
        fixed
        className='h-5 w-5 mx-2.5 bg-accent'
        open={isLogoutLoading}
      />
    </>
  )
}

export default HeaderAuth
